import { useCallback, useEffect, useState } from 'react'
import cn from 'classnames'

import scrollToElement from 'utils/scroll-to-element'

import ButtonOrange from 'components/ui/button/_color/_orange'
import { InputRange } from 'components/ui/input-range'

import { useTranslation } from 'utils/use-translation'

import styles from './styles.module.scss'

import IconOk from './icon-ok.svg'
import IconNote from './icon-note.svg'
import IconGear from './icon-gear.svg'
import IconMoney from './icon-money.svg'

export default function CalculateProfit() {
  const { t } = useTranslation()

  const [loanValue, setLoanValue] = useState(3000)
  const [customerValue, setCustomerValue] = useState(10000)
  const [termValue, setTermValue] = useState(4)
  const [resultValue, setResultValue] = useState(0)

  const handleScrollToForm = useCallback(() => {
    scrollToElement('contactForm')
  }, [])
  const loanMax = 100000
  const loanMin = 100
  const apr = 0.024

  useEffect(() => {
    const result = Math.round(((loanValue * customerValue * apr) / 12) * termValue).toString()
    setResultValue(result.replace(/(\d)(?=(\d\d\d)+([^\d]|$))/g, '$1 '))
  }, [loanValue, termValue, customerValue])

  const handleChangeValue = (e, setValue) => {
    if (!isNaN(e)) {
      setValue(Math.round(e * 10) / 10)
      return
    }
    let value = e.target.value
    if (isNaN(value)) return
    value = Math.round(value * 10) / 10
    if (+value <= +e.target.min) value = +e.target.min
    if (+value > +e.target.max) value = +e.target.max
    setValue(value)
  }

  return (
    <div className={cn('container', styles.container)}>
      <div className={styles.title}>{t('calculate_profit')}</div>
      <div className={styles.calculationContainer}>
        <div className={styles.inputsWrapper}>
          <div className={styles.sliderWrapper}>
            <div className={styles.sliderContainer}>
              <div className={styles.rangeWrapper}>
                <InputRange
                  maxValue={loanMax}
                  minValue={loanMin}
                  step={1}
                  name="loan"
                  value={loanValue}
                  onChange={(e) => handleChangeValue(e, setLoanValue, 'loan')}
                  mainColor={t('highlight_color')}
                  secondColor={t('color_white')}
                />
              </div>
              <output name="loanAmount" id="loanAmount">
                <input
                  type="number"
                  className={cn(styles.input, styles.inputLoan)}
                  value={loanValue}
                  step={1}
                  max={loanMax}
                  min={loanMin}
                  onChange={(e) => handleChangeValue(e, setLoanValue, 'loan')}
                />{' '}
                USDT
              </output>
              <label>{t('average_loan_amount')}</label>
            </div>
            <div className={styles.rangeLabel}>
              <span>{t('loan_range')}</span>
              <span>{t('loan_range_max')}</span>
            </div>
          </div>
          <div className={styles.sliderWrapper}>
            <div className={styles.sliderContainer}>
              <div className={styles.rangeWrapper}>
                <InputRange
                  maxValue={100000}
                  minValue={10}
                  step={10}
                  name="customer"
                  value={customerValue}
                  onChange={(customerValue) => handleChangeValue(customerValue, setCustomerValue, 'customer')}
                  mainColor={t('highlight_color')}
                  secondColor={t('color_white')}
                />
              </div>
              <output name="customerAmount" id="customerAmount" htmlFor="customer">
                <input
                  type="number"
                  className={cn(styles.input, styles.inputCustomer)}
                  value={customerValue}
                  step={10}
                  max={100000}
                  min={10}
                  onChange={(e) => handleChangeValue(e, setCustomerValue, 'customer')}
                />
              </output>
              <label>{t('number_of_clients')}</label>
            </div>
            <div className={styles.rangeLabel}>
              <span>{t('clients_range')}</span>
              <span>{t('clients_range_max')}</span>
            </div>
          </div>
          <div className={styles.sliderWrapper}>
            <div className={styles.sliderContainer}>
              <div className={styles.rangeWrapper}>
                <InputRange
                  maxValue={24}
                  minValue={2}
                  step={1}
                  name="term"
                  value={termValue}
                  onChange={(termValue) => handleChangeValue(termValue, setTermValue, 'term')}
                  mainColor={t('highlight_color')}
                  secondColor={t('color_white')}
                />
              </div>
              <output name="termAmount" id="termAmount" htmlFor="term">
                <input
                  type="number"
                  className={cn(styles.input)}
                  value={termValue}
                  step={1}
                  max={24}
                  min={2}
                  onChange={(e) => handleChangeValue(e, setTermValue, 'term')}
                />
                {t('loan_duration_months')}
              </output>
              <label>{t('loan_duration')}</label>
            </div>
            <div className={styles.rangeLabel}>
              <span>{t('loan_duration_min')}</span>
              <span>{t('loan_duration_max')}</span>
            </div>
          </div>
        </div>
        <div className={styles.resultContainer}>
          <ul>
            <li>
              <img src={IconOk} alt="Ok" width={21} height={18} />
              {t('predicted_conversion')}
            </li>
            <li>
              <img src={IconOk} alt="Ok" width={21} height={18} />
              {t('loan_swap_prediction')}
            </li>
            <li>
              <img src={IconOk} alt="Ok" width={21} height={18} />
              {t('guaranteed_profit_month_2')}
            </li>
          </ul>
          <div className={styles.resultTitle}>2,4% APR</div>
          <div className={styles.youWillMakeBlock}>
            <p>
              {t('in')} {termValue} {t('months_you_will_make')}
            </p>
            <div className={styles.coin}>{resultValue} USDT</div>
          </div>
        </div>
      </div>
      <p>{t('support_team_ready_to_help')}</p>
      <ButtonOrange onClick={handleScrollToForm}>{t('contact_us')}</ButtonOrange>

      <section className={styles.iconsWrapper}>
        <div className={styles.icon}>
          <img src={IconNote} alt="IconNote" width={120} height={120} />
          <p dangerouslySetInnerHTML={{ __html: t('integration_contact') }} />
        </div>
        <div className={styles.icon}>
          <img src={IconGear} alt="IconGear" width={121} height={120} />
          <p>{t('integration_solution')}<br />{t('integration_time')}</p>
        </div>
        <div className={styles.icon}>
          <img className={styles.iconMoney} src={IconMoney} alt="IconMoney" width={100} height={100} />
          <p>{t('make_money')}<br />{t('coinrabbit_business')}</p>
        </div>
      </section>
    </div>
  )
}
